import { union } from 'lodash';

import { CompanyServiceLevels } from '../../company/constants';

const LIQUOR = 'Liquor';
const SGWS_COMPANY_NAME = 'southern glazers';

export const isSgwsCompany = (company) => (
    !!company && company.company.toLowerCase() === SGWS_COMPANY_NAME
);

/**
 * Complete companies often carry several product types, but a user created for one
 * should only get liquor by default. The person creating the user can still add the
 * other types afterwards - this is a default, not a restriction.
 *
 * @param {string} serviceLevel the level the user is being created with
 * @param {string[]} companyTypes the product types the company itself carries
 * @returns {string[]} the types to pre-select for the user
 */
export const defaultProductTypes = (serviceLevel, companyTypes) => (
    serviceLevel === CompanyServiceLevels.COMPLETE && companyTypes.indexOf(LIQUOR) >= 0
        ? [LIQUOR]
        : companyTypes
);

/**
 * A Complete company is expected to always carry liquor. There are none like this in
 * the database today, so a company that trips this is a bad company record rather
 * than a user we should be creating. Users cannot be saved against one.
 *
 * @param {object} company the company selected on the form
 * @returns {boolean} true when the company is Complete but carries no liquor
 */
export const isCompleteWithoutLiquor = (company) => (
    !!company
    && company.service_level === CompanyServiceLevels.COMPLETE
    && company.service_product_type.split(',').indexOf(LIQUOR) < 0
);

/**
 * Power user only means anything at the Complete level - users at any other level do
 * not have access to the system the flag governs. On top of that, Southern Glazers
 * Complete users are always power users.
 *
 * So the toggle is only the operator's to set for a Complete user at any other
 * company; everywhere else it is decided for them and locked.
 *
 * @param {object} company the company selected on the form
 * @param {string} serviceLevel the level currently chosen on the form
 * @returns {boolean} true when the operator may not set the flag themselves
 */
export const isPowerUserLocked = (company, serviceLevel) => (
    serviceLevel !== CompanyServiceLevels.COMPLETE || isSgwsCompany(company)
);

/**
 * The value to force when isPowerUserLocked says the operator has no say: on for
 * SGWS Complete users, off for anyone below Complete.
 *
 * @param {object} company the company selected on the form
 * @param {string} serviceLevel the level currently chosen on the form
 * @returns {boolean} the value power_user must hold
 */
export const forcedPowerUser = (company, serviceLevel) => (
    serviceLevel === CompanyServiceLevels.COMPLETE && isSgwsCompany(company)
);

/**
 * A user should not be given access to a product type their company does not carry,
 * so the Services dropdown only offers what the company has.
 *
 * Anything the user already holds is kept in the list even when the company no longer
 * carries it. Otherwise an existing value would disappear from the dropdown while
 * remaining set on the user, leaving no way to see or remove it.
 *
 * @param {object} company the company selected on the form
 * @param {string[]} selected the types currently set on the user
 * @returns {string[]} the types to offer in the dropdown
 */
export const availableProductTypes = (company, selected = []) => union(
    !!company ? company.service_product_type.split(',') : [],
    selected || [],
);
